import { ChangeEvent, useRef, useState } from 'react'

import { createId } from '@paralleldrive/cuid2'
import { IconSignature } from '@tabler/icons-react'
import { Image } from 'antd'
import styled from 'styled-components'

import { Button } from '../shared-components/BUI/Button'
import { Input } from '../shared-components/BUI/Input'
import { Modal } from '../shared-components/BUI/Modal'
import { Flexbox } from '../shared-components/Flexbox'
import { SigCanvas, SignatureBox } from '../shared-components/SignatureBox'

import {
  FileUploadResult,
  useAttachmentUpload,
} from '@/hooks/useAttachmentUpload'

import { AttachmentType, Resource } from 'types/api.graphql'

const SignaturePreview = styled(Image)`
  display: block;
  max-height: 100px;
`
const SignaturePreviewWrapper = styled.div`
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
`

export type SignatureData = {
  signee: string
  url?: string
  key?: string
  time?: Date
  attachmentId?: string
}

type Props = {
  value?: SignatureData
  onChange?: (value: SignatureData) => void
  projectId: string
  resource: Resource
}

export const SignatureAttachmentUploader: React.FC<Props> = ({
  value,
  onChange,
  projectId,
  resource,
}) => {
  const sigCanvas = useRef<SigCanvas>(undefined)
  const [canvasDirty, setCanvasDirty] = useState(false)
  const [modalOpen, toggleModalOpen] = useState(false)

  const openModal = () => toggleModalOpen(true)
  const closeModal = () => toggleModalOpen(false)

  const handleRedo = () => {
    setCanvasDirty(false)
    sigCanvas.current?.clear()
    onChange({ ...value, key: null, url: null })
    openModal()
    sigCanvas.current?.clear()
  }

  const onUpload = (result: FileUploadResult) => {
    closeModal()
    onChange({
      ...value,
      attachmentId: result.attachmentId,
      key: result.key,
      url: result.url,
      time: new Date(),
    })
  }

  const onSigneeChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, signee: e.target.value })
  }

  return (
    <>
      <Flexbox gap="0.5rem" alignItems="center">
        <Input
          style={{ maxWidth: '400px' }}
          value={value?.signee}
          onInput={onSigneeChange}
          onChange={onSigneeChange}
          placeholder="Signed by"
        />
        <div>
          {value?.url ? (
            <Button onClick={handleRedo}>Redo</Button>
          ) : (
            <Button onClick={openModal} icon={<IconSignature size={16} />}>
              Sign
            </Button>
          )}
        </div>
      </Flexbox>

      {value?.url && (
        <SignaturePreviewWrapper>
          <SignaturePreview src={value.url} alt="signature" />
        </SignaturePreviewWrapper>
      )}

      <SigningAttachmentModal
        visible={modalOpen}
        onUpload={onUpload}
        closeModal={closeModal}
        projectId={projectId}
        resource={resource}
        sigCanvas={sigCanvas}
        canvasDirty={canvasDirty}
        setCanvasDirty={setCanvasDirty}
      />
    </>
  )
}

type ModalProps = {
  visible: boolean
  onUpload: (result: FileUploadResult) => void
  closeModal: () => void
  projectId: string
  resource: Resource
  sigCanvas: React.RefObject<SigCanvas>
  canvasDirty: boolean
  setCanvasDirty: (dirty: boolean) => void
}

export const SigningAttachmentModal: React.FC<ModalProps> = ({
  visible,
  onUpload,
  closeModal,
  projectId,
  resource,
  sigCanvas,
  canvasDirty,
  setCanvasDirty,
}) => {
  const { uploadAttachment } = useAttachmentUpload()

  const [isLoading, setIsLoading] = useState(false)

  const clearCanvas = () => {
    setCanvasDirty(false)
    sigCanvas.current?.clear()
  }

  const onCancel = () => {
    clearCanvas()
    closeModal()
  }

  const onAdd = async () => {
    setIsLoading(true)

    const blob = sigCanvas.current?.toDataURL('image/png')
    const base64Data = Buffer.from(
      blob.replace(/^data:image\/\w+;base64,/, ''),
      'base64'
    )

    const file = new File([base64Data], `signature-${createId()}`, {
      type: 'image/png',
    })

    await uploadAttachment(file, {
      attachmentType: AttachmentType.Signature,
      attachmentResource: resource ?? Resource.TemplateResponse,
      createAttachment: true,
      onUpload,
      mutationVariables: { projectId },
    })

    setIsLoading(false)
    clearCanvas()
  }

  return (
    <Modal
      title="Signature pad"
      open={visible}
      centered
      onCancel={onCancel}
      footerLayout="spaced"
      footer={
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            variant="primary"
            onClick={onAdd}
            loading={isLoading}
            disabled={!canvasDirty}
          >
            Confirm
          </Button>
        </>
      }
    >
      <SignatureBox
        labelOverride="Please sign in the space provided below"
        canvasRef={sigCanvas}
        onCanvasChange={() => setCanvasDirty(true)}
      />
    </Modal>
  )
}
